import React from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Fade,
  Slide
} from '@mui/material';
import {
  EmojiEvents as TrophyIcon,
  RestartAlt as RestartIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import "./Guess.css";

const ResultCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
  borderRadius: theme.spacing(3),
  padding: theme.spacing(3),
  textAlign: 'center',
  boxShadow: '0 10px 30px rgba(0,0,0,0.2)',
  border: '2px solid rgba(255,255,255,0.3)'
}));

const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(2, 6),
  fontSize: '1.2rem',
  fontWeight: 'bold',
  textTransform: 'none',
  margin: theme.spacing(1),
  transition: 'all 0.3s ease',
  background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
  color: 'white',
  border: '2px solid rgba(255,255,255,0.3)',
  boxShadow: '0 8px 20px rgba(0,0,0,0.15)',
  minWidth: '180px',
  height: '56px',
  whiteSpace: 'nowrap',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(1),
  '& .MuiButton-startIcon': {
    margin: 0,
    marginRight: theme.spacing(1)
  },
  '&:hover': {
    transform: 'translateY(-3px)',
    boxShadow: '0 12px 30px rgba(79, 172, 254, 0.4)',
    borderColor: 'rgba(255,255,255,0.6)',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  },
  '&:active': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 15px rgba(0,0,0,0.2)',
  },
  '&:disabled': {
    opacity: 0.6,
    transform: 'none'
  }
}));

const Result = props => {
  // Find current player's rank
  const currentPlayerRank = props.toprankers.findIndex(player => 
    player.userName === props.user && player.score === props.score
  ) + 1;
  
  // Get top 3 players
  const top3Players = props.toprankers.slice(0, 3);
  
  return (
    <Slide in direction="up" timeout={600}>
      <Box sx={{ textAlign: 'center', p: 2 }}>
        <ResultCard>
          <Fade in timeout={1000}>
            <Box>
              <Typography 
                variant="h4" 
                sx={{ 
                  color: '#333',
                  fontWeight: 'bold',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1
                }}
              >
                🎯 Game Over!
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  color: '#666',
                  mb: 2
                }}
              >
                Answer: <span style={{ color: '#e74c3c', fontWeight: 'bold' }}>{props.Answer}</span>
              </Typography>
              
              <Chip 
                label={`Your Score: ${props.score.toLocaleString()}`}
                icon={<TrophyIcon />}
                sx={{ 
                  background: 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)',
                  color: '#333',
                  fontSize: '1.2rem',
                  fontWeight: 'bold',
                  px: 3,
                  py: 2,
                  mb: 2
                }}
              />
              
              {/* Current Player Rank */}
              {currentPlayerRank > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Chip 
                    label={`Your Rank: #${currentPlayerRank}`}
                    sx={{ 
                      background: currentPlayerRank <= 3 
                        ? 'linear-gradient(135deg, #ffd700 0%, #ffed4e 100%)'
                        : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: currentPlayerRank <= 3 ? '#333' : 'white',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      px: 2,
                      py: 1
                    }}
                  />
                </Box>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2 }}>
                <ActionButton
                  onClick={props.newgame}
                  startIcon={<RestartIcon />}
                >
                  New Game
                </ActionButton>
              </Box>
            </Box>
          </Fade>
        </ResultCard>
        
        {/* Top 3 Players - Always Visible */}
        <Fade in timeout={800}>
          <Card sx={{ mt: 3, borderRadius: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: 0 }}>
              <Box sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                p: 2
              }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrophyIcon /> 🏆 Top 3 Champions
                </Typography>
              </Box>
              
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ background: 'rgba(0,0,0,0.05)' }}>
                      <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Rank</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Player</TableCell>
                      <TableCell sx={{ fontWeight: 'bold', color: '#333' }}>Score</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {top3Players.map((topranker, index) => (
                      <TableRow 
                        key={index}
                        sx={{ 
                          background: topranker.userName === props.user && topranker.score === props.score
                            ? 'linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)'
                            : index % 2 === 0 ? 'rgba(0,0,0,0.02)' : 'transparent',
                          '&:hover': { backgroundColor: 'rgba(102, 126, 234, 0.1)' }
                        }}
                      >
                        <TableCell>
                          <Chip 
                            label={index + 1}
                            size="small"
                            sx={{ 
                              background: `linear-gradient(135deg, ${
                                index === 0 ? '#ffd700, #ffed4e' : 
                                index === 1 ? '#c0c0c0, #e8e8e8' : 
                                '#cd7f32, #ff9f40'
                              })`,
                              color: '#333',
                              fontWeight: 'bold',
                              fontSize: '0.9rem'
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ 
                          color: '#333', 
                          fontWeight: topranker.userName === props.user && topranker.score === props.score ? 'bold' : '500'
                        }}>
                          {topranker.userName}
                          {topranker.userName === props.user && topranker.score === props.score && (
                            <Chip 
                              label="YOU" 
                              size="small" 
                              sx={{ 
                                ml: 1, 
                                background: '#667eea', 
                                color: 'white',
                                fontSize: '0.7rem'
                              }} 
                            />
                          )}
                        </TableCell>
                        <TableCell sx={{ 
                          color: '#333', 
                          fontWeight: 'bold',
                          fontSize: '1rem'
                        }}>
                          {topranker.score.toLocaleString()}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Fade>
      </Box>
    </Slide>
  );
};

export default Result;
